import { useState, type KeyboardEvent, type ReactNode } from "react";
import { LoadingBlock } from "./Spinner";
import { EmptyState } from "./EmptyState";

export interface Column<T> {
  /** Sütun kimliği — React key olarak da kullanılır. */
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** Dar ekranda gizlenir (öncelikli olmayan sütunlar). */
  hideOnMobile?: boolean;
  /** Sağa hizalı sayısal sütun. */
  numeric?: boolean;
  className?: string;
}

interface DataTableProps<T> {
  rows: readonly T[];
  columns: ReadonlyArray<Column<T>>;
  rowKey: (row: T) => string;
  loading?: boolean;
  /** Türkçe hata mesajı — varsa tablo yerine gösterilir. */
  error?: string | null;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Satıra tıklanınca çalışır; verilirse satır klavye ile de erişilebilir olur. */
  onRowClick?: (row: T) => void;
  /** Tablo başlığı — ekran okuyucu için zorunlu bağlam. */
  caption: string;
  footer?: ReactNode;
  /**
   * Uzun listede ilk N satır gösterilir, altında "Tümünü göster" düğmesi çıkar.
   * Verilmezse hepsi gösterilir. Toplamlar/dışa aktarım her zaman TÜM satırlardan.
   */
  initialRows?: number;
}

/**
 * Yoğun bilgi tablosu.
 *
 * Mobil/tablet: yatay kaydırma yerine önemsiz sütunlar gizlenir (atölyede
 * tablet kullanılıyor, yatay kaydırma parmakla iş görmüyor).
 * Erişilebilirlik: <caption>, scope="col", tıklanabilir satırda tabIndex + Enter.
 */
export function DataTable<T>({
  rows,
  columns,
  rowKey,
  loading = false,
  error = null,
  emptyTitle = "Kayıt yok",
  emptyDescription,
  onRowClick,
  caption,
  footer,
  initialRows,
}: DataTableProps<T>) {
  const [expanded, setExpanded] = useState(false);
  if (loading) return <LoadingBlock />;

  if (error) {
    return (
      <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
        {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  const limited = Boolean(initialRows && !expanded && rows.length > initialRows);
  const canCollapse = Boolean(initialRows && expanded && rows.length > initialRows);

  return (
    <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200/80">
      <table className="table-dense">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-gray-50">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={[
                  c.hideOnMobile ? "hidden sm:table-cell" : "",
                  c.numeric ? "text-right" : "",
                  c.className ?? "",
                ].join(" ")}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {(limited ? rows.slice(0, initialRows) : rows).map((row) => {
            const clickable = Boolean(onRowClick);
            return (
              <tr
                key={rowKey(row)}
                /**
                 * ★ ODAK GÖSTERGESİ `outline` İLE, `ring` İLE DEĞİL.
                 * index.css'teki genel `:focus-visible` kuralı `ring-2` kullanıyor;
                 * `ring`, `box-shadow` üzerine kurulu ve `border-collapse: collapse`
                 * olan bir tabloda `<tr>`'nin gölgesi hücrelerin ARKASINDA kalıyor —
                 * klavyeyle gezen kullanıcı hangi satırda olduğunu GÖREMİYORDU.
                 * `outline` çakışmadan çizilir; zemin değişimi de ikinci bir işaret.
                 */
                className={
                  clickable
                    ? "cursor-pointer hover:bg-primary-50 focus-visible:bg-primary-50 " +
                      "focus-visible:outline focus-visible:outline-2 " +
                      "focus-visible:-outline-offset-2 focus-visible:outline-primary-600"
                    : undefined
                }
                {...(clickable
                  ? {
                      tabIndex: 0,
                      /**
                       * ★ `role="button"` BİLEREK YOK. Konulduğunda satırın `row`
                       * anlamsallığı siliniyor ve ekran okuyucu tabloyu tablo olarak
                       * duyuramıyor: "3. satır, 5 sütun" yerine yalnızca "düğme"
                       * diyor, hücre başlıkları kayboluyor. Klavye etkileşimi için
                       * gerekli de değil — `tabIndex` odaklanmayı, `onKeyDown`
                       * Enter/Space'i zaten sağlıyor.
                       */
                      onClick: () => onRowClick?.(row),
                      onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick?.(row);
                        }
                      },
                    }
                  : {})}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={[
                      c.hideOnMobile ? "hidden sm:table-cell" : "",
                      c.numeric ? "text-right tabular-nums" : "",
                      c.className ?? "",
                    ].join(" ")}
                  >
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        {limited || canCollapse ? (
          <tbody>
            <tr className="noprint">
              <td colSpan={columns.length} className="px-3 py-2 text-center">
                <button
                  type="button"
                  onClick={() => setExpanded((e) => !e)}
                  className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-primary-700 hover:bg-primary-50"
                >
                  {limited
                    ? `Tümünü göster (${rows.length - (initialRows ?? 0)} satır daha)`
                    : "Daha az göster"}
                </button>
              </td>
            </tr>
          </tbody>
        ) : null}
        {footer ? (
          <tfoot className="bg-gray-50">
            <tr>
              <td colSpan={columns.length} className="px-3 py-2 text-xs text-gray-600">
                {footer}
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

export default DataTable;
